/**
 * OrgContext - Active Organization Context
 *
 * Stores the user's currently selected organization and makes it
 * available throughout the app. The selection is persisted in
 * localStorage and sent as `X-Org-Id` on all API requests when set.
 *
 * Users who hold both a personal subscription and belong to a team org
 * use this selector to specify which subscription should be billed.
 */

import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

const STORAGE_KEY = 'guardrail_active_org_id';

interface OrgContextType {
  /** Currently selected org ID (null = personal subscription) */
  activeOrgId: string | null;
  /** Set the active org (null = use personal subscription) */
  setActiveOrg: (orgId: string | null) => void;
  /** Header value to include in API requests (empty object if personal) */
  orgHeader: Record<string, string>;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

export function OrgProvider({ children }: { children: ReactNode }) {
  const [activeOrgId, setActiveOrgId] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY)
  );

  const setActiveOrg = useCallback((orgId: string | null) => {
    setActiveOrgId(orgId);
    if (orgId) localStorage.setItem(STORAGE_KEY, orgId);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const orgHeader: Record<string, string> = activeOrgId ? { 'X-Org-Id': activeOrgId } : {};

  return (
    <OrgContext.Provider value={{ activeOrgId, setActiveOrg, orgHeader }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrgContext() {
  const context = useContext(OrgContext);
  if (!context) throw new Error('useOrgContext must be used within OrgProvider');
  return context;
}
